import React from 'react';

import Text from '~/components/core/TextComponents/Text';

import type { ProgressStatus } from './types';
import { useElapsedSeconds } from './useElapsedSeconds';

const SECONDS_PER_MINUTE = 60;

const formatDuration = (seconds: number): string =>
  seconds < SECONDS_PER_MINUTE
    ? `${seconds}s`
    : `${Math.floor(seconds / SECONDS_PER_MINUTE)}m ${seconds % SECONDS_PER_MINUTE}s`;

/**
 * How long the run has been going, or how long it took.
 *
 * Its own component so the once-a-second tick re-renders this text alone, leaving the message beside
 * it untouched. A run in flight counts against the clock, since its events go quiet for long stretches
 * between steps; a finished one reports the span between its first and last event, which no browser
 * clock can skew.
 */
export const ProgressDuration: React.FC<{ status: ProgressStatus }> = ({ status }) => {
  const { startedAt, durationSeconds, isRunning } = status;
  const elapsedSeconds = useElapsedSeconds(startedAt, isRunning);

  const seconds = isRunning ? elapsedSeconds : durationSeconds;
  if (seconds === null) {
    return null;
  }

  return (
    <Text variant={Text.VARIANTS.XS} className="shrink-0 text-grey-400">
      {isRunning ? `Running ${formatDuration(seconds)}` : `Processed ${formatDuration(seconds)}`}
    </Text>
  );
};

export default ProgressDuration;
